/**
 * **小地图的触摸手势数学** —— 纯函数，零 DOM 依赖。
 *
 * ## 为什么需要自己实现（不是配置问题）
 *
 * `@vue-flow/minimap` 的 `pannable` / `zoomable` **在触摸设备上根本不工作**。
 * 读它的源码就能看到，两个处理函数各自开头有一道**事件类型闸门**：
 *
 * ```js
 * const panHandler = (event) => {
 *   if (event.sourceEvent.type !== 'mousemove' || …) return   // ← 触摸是 touchmove
 * }
 * const zoomHandler = (event) => {
 *   if (event.sourceEvent.type !== 'wheel' || …) return       // ← 触摸是 touchmove
 * }
 * ```
 *
 * 也就是说它们是**鼠标专用**的：滚轮缩放、按住拖平移，仅此而已。
 * 触摸时 d3-zoom 仍然会收下事件（于是画布**不会**跟着平移 —— 这一点是对的），
 * 但它自己什么也不做 → 表现就是「小地图完全点不动」。
 *
 * 所以正确的修法不是调 CSS（`touch-action` 早就设过了，没用），
 * 而是**自己接管触摸手势**，把位移/缩放换算成画布的 viewport。
 *
 * ## 坐标换算的依据
 *
 * 小地图的 `viewBox` 是这么算出来的（源码 `viewBox` computed）：
 *
 * ```
 * viewScale  = max(bbox.width / elWidth, bbox.height / elHeight)
 * viewWidth  = viewScale * elWidth          // ← 与元素**同比**
 * viewHeight = viewScale * elHeight
 * offset     = offsetScale * viewScale
 * viewBox    = { x: …, y: …, width: viewWidth + 2*offset, height: viewHeight + 2*offset }
 * ```
 *
 * 关键点：`viewWidth/elWidth === viewHeight/elHeight`，而且四周加的 `offset`
 * 是**对称**的 —— 所以 `viewBox` 与元素的宽高比完全一致，
 * 不会出现 `preserveAspectRatio` 的留白（letterbox）。
 * 于是「小地图上的像素 ↔ 画布坐标」就是一条干净的线性关系：
 *
 * ```
 * 画布坐标 = viewBox.x + 小地图像素 * (viewBox.width / 元素像素宽)
 * ```
 *
 * 这个比例叫 `unitsPerPx`，下面两个函数都用它。
 */

/** 一个 viewport（和 `graph/flowStore.ts` 的 `Viewport` 同形） */
export interface Viewport {
  x: number
  y: number
  zoom: number
}

/** 小地图的几何事实 —— 全部能直接从 DOM 读出来 */
export interface MiniMapMetrics {
  /** svg 上的 `viewBox` 属性（画布坐标） */
  viewBox: { x: number; y: number; width: number; height: number }
  /** svg 的 CSS 像素宽 / 高（不是 viewBox，是元素实际占的像素） */
  elementWidth: number
  elementHeight: number
}

/** 和 VueFlow 的 `minZoom` / `maxZoom` 保持一致，别让手势绕过去 */
export const MIN_ZOOM = 0.2
export const MAX_ZOOM = 3

function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
}

/** 画布单位 / 小地图像素。元素宽度为 0（还没布局）时返回 0，调用方据此跳过 */
export function unitsPerPx(metrics: MiniMapMetrics): number {
  if (!metrics.elementWidth || !metrics.viewBox.width) return 0
  return metrics.viewBox.width / metrics.elementWidth
}

/**
 * 小地图上的一个点（相对 svg 左上角的像素）→ 画布坐标。
 *
 * 双指捏合时用它算「手指中间那个画布点」，好让缩放绕着自己进行 ——
 * 不这么做的话画面会朝左上角跑。
 */
export function flowAtMiniMapPoint(
  metrics: MiniMapMetrics,
  localX: number,
  localY: number,
): { x: number; y: number } {
  const scaleX = metrics.elementWidth ? metrics.viewBox.width / metrics.elementWidth : 0
  const scaleY = metrics.elementHeight ? metrics.viewBox.height / metrics.elementHeight : 0
  return {
    x: metrics.viewBox.x + localX * scaleX,
    y: metrics.viewBox.y + localY * scaleY,
  }
}

/**
 * 单指拖动 → 平移画布。
 *
 * ⚠️ 系数**照抄 Vue Flow 的鼠标路径**（`moveScale = viewScale * max(1, zoom)`），
 * 这样手指拖和鼠标拖的手感一致 —— 两套手势同一件事却跑不同速度会很怪。
 * `max(1, zoom)` 那一项是他们在 `zoom < 1` 时刻意不乘 zoom（否则拖起来太黏），
 * 属于手感调校，不是推导结果，所以照抄而不是「纠正」。
 */
export function panByMiniMapDrag(
  viewport: Viewport,
  metrics: MiniMapMetrics,
  dxPx: number,
  dyPx: number,
): Viewport {
  const scale = unitsPerPx(metrics)
  if (!scale) return viewport
  const move = scale * Math.max(1, viewport.zoom)
  return {
    x: viewport.x - dxPx * move,
    y: viewport.y - dyPx * move,
    zoom: viewport.zoom,
  }
}

/**
 * 双指捏合 → 绕 `anchor`（画布坐标）缩放。
 *
 * 推导：画布坐标 `a` 在屏幕上的位置是 `a * zoom + xy`。
 * 要求缩放前后 `anchor` 的屏幕位置不变：
 *
 * ```
 * a * z0 + xy0 = a * z1 + xy1
 * xy1 = xy0 + a * (z0 - z1)
 * ```
 *
 * `factor` 是「两指距离的新值 / 旧值」—— 与 d3-zoom 的约定一致。
 */
export function pinchAround(
  viewport: Viewport,
  factor: number,
  anchor: { x: number; y: number },
): Viewport {
  const zoom = clampZoom(viewport.zoom * factor)
  // clamp 之后要用**实际**生效的比例，否则缩放到极限时画面会漂
  const applied = zoom / viewport.zoom
  if (applied === 1) return viewport
  return {
    x: viewport.x + anchor.x * viewport.zoom * (1 - applied),
    y: viewport.y + anchor.y * viewport.zoom * (1 - applied),
    zoom,
  }
}

/**
 * 双指手势是"想缩放"还是"只想平移"。
 *
 * ⚠️ 两指手势天然**同时**产生位移和距离变化 —— 想平移时两根手指的距离
 * 总会有几个像素的抖动，于是画面一边挪一边轻微缩放，手感是"糊"的
 * （实测反馈：「双指拖回和双指缩放有点重叠」）。
 *
 * 判据：**从手势开始算**，两指距离相对变化超过阈值才算"想缩放"；
 * 没超过就只平移（factor 给 1，`pinchAround` 会原样返回）。
 *
 * ⚠️ 用**从起点**算的总变化、而不是逐帧变化：逐帧的抖动很小、永远够不到
 * 阈值，而"想缩放"这个意图一旦成立就该**锁住**（`armed`），
 * 否则手指停一下再动又会掉回平移，切换感很明显。
 *
 * @param startDistance 手势开始时两指的距离
 * @param distance      当前两指距离
 * @param armed         本次手势是否已经判定为"在缩放"
 */
export const ZOOM_ARM_RATIO = 0.12

export function isZoomIntent(startDistance: number, distance: number, armed: boolean): boolean {
  if (armed) return true
  if (startDistance <= 0) return false
  return Math.abs(distance / startDistance - 1) > ZOOM_ARM_RATIO
}

/** 两指之间的距离（用来算 pinch 的 factor 与中点） */
export function distanceBetween(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

export function midpointOf(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}
